import { useMemo } from 'react';
import { useProcessingDataExportDocument } from 'src/suitability-report/hooks/useProcessingDataExportDocument';
import { BackgroundProcess, FileData } from 'src/data-capture/hooks/file-uploads/types';
import useTemplates from 'src/data-capture/hooks/templates/useTemplates';
import { labelFromReportType, labelFromTemplate } from 'src/suitability-report/utils/templateLabel';

type SuitabilityReportType = 'NO_CHANGE';

type SuitabilityReport = {
  id: string;
  clientId: string;
  backgroundProcessId?: string;
  backgroundProcess?: BackgroundProcess;
  reportType: SuitabilityReportType;
  createdAt: string;
  currentSnapshotId?: string;
  previousSnapshotId?: string;
  templateId?: string;
  supportingDocumentIds: string[];
  reportFileId?: number;
  reportFile: FileData | null;
  snapshotHashes: string[];
  uniqueHash: string;
  hasTitlePage: boolean;
  hasTableOfContents: boolean;
  usesDeepThinking: boolean;
  displayName: string;
  isGenerated: true;
};

type Props = {
  clientId?: string;
};

export const useSuitabilityReports = ({ clientId }: Props = {}) => {
  const { items, isLoading, isError, refetch, error } = useProcessingDataExportDocument<
    Omit<SuitabilityReport, 'displayName' | 'isGenerated'>
  >({
    clientId,
    resource: 'suitability-reports',
  });

  const { files: templates, isLoading: isTemplatesLoading } = useTemplates({
    templateType: 'SUITABILITY_REPORT',
  });

  const suitabilityReports = useMemo(
    () =>
      items.map(report => {
        const template = templates.find(t => t.id === report.templateId);
        return {
          ...report,
          isGenerated: true,
          displayName: labelFromTemplate(template) || labelFromReportType(report.reportType),
        };
      }),
    [items, templates],
  );

  return {
    suitabilityReports,
    isLoading: isLoading || isTemplatesLoading,
    isError,
    error,
    refetch,
  };
};
